/**
 * @file index.tsx
 *
 * @fileoverview Shows the job preferences widget when in view mode.
 */
import React, { useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { OrganizationWidgetsBoxLabel } from '~/jobiqo/components/Organization/Widgets/BoxLabel';
import { EditLink } from '~/jobiqo/components/Organization/Widgets/EditLink';
import { OrganizationProfile } from '~/jobiqo/types/types';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Box } from '~/vcl/atoms/07-box';
import { StringJoiner } from '~/vcl/atoms/13-text/01-string-joiner';
import { WYSIWYGText } from '~/vcl/atoms/13-text/02-WYSIWYG';
import { Text } from '~/vcl/atoms/13-text/03-text';
import {
  Expander,
  ExpanderContent,
  ExpanderTrigger,
} from '~/vcl/atoms/22-expander';
import { BriefcaseIcon } from '~/vcl/icons/Briefcase';
import EducationIcon from '~/jobiqo/components/Icons/EducationIcon';
import { OfficeIcon } from '~/vcl/icons/Office';
import { LinkButton } from '~/vcl/atoms/01-forms/01-button/02-link-button';
import Link from '~/jobiqo/components/Link';
import NextLink from 'next/link';
import OrganizationDetailAboutExtraInfoSlot from '~/jobiqo/components/Slots/OrganizationDetailAboutExtraInfoSlot';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { PathContext } from '~/jobiqo/context/app';
import { PathContextInterface } from '~/jobiqo/models/app';
import {
  normalizeSiteOrigin,
  transformExternalAnchors,
} from '~/jobiqo/lib/html/externalAnchors';

export interface OrganizationAboutWidgetViewProps {
  /**
   * The current organization profile.
   */
  organization: OrganizationProfile;
  /**
   * The current box id.
   */
  boxId: string;
  /**
   * Whether the widget content is trimmed.
   */
  trimmed?: boolean;
}

export const OrganizationAboutWidgetView = ({
  organization,
  boxId,
  trimmed = false,
}: OrganizationAboutWidgetViewProps) => {
  const intl = useIntl();
  const { ORGANIZATIONS } = useFrontendConfig();
  const { host }: PathContextInterface = useContext(PathContext);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const siteOrigin =
    normalizeSiteOrigin(process.env.CLIENT_URL) ?? normalizeSiteOrigin(host);
  const presentation = transformExternalAnchors(
    organization.presentation ?? '',
    siteOrigin,
    {
      addNoFollow:
        ORGANIZATIONS.DETAIL_NOFOLLOW_EXTERNAL_PRESENTATION_LINKS ?? false,
      convertToPlainText:
        ORGANIZATIONS.DETAIL_PLAINTEXT_EXTERNAL_PRESENTATION_LINKS ?? false,
    },
  );

  return (
    <Box
      label={
        <OrganizationWidgetsBoxLabel>
          {intl.formatMessage({
            id: 'organization_about_label',
            defaultMessage: 'About us',
          })}
        </OrganizationWidgetsBoxLabel>
      }
    >
      <FlexItems justifyContent="flex-end">
        <EditLink
          organization={organization}
          boxId={boxId}
          boxHasTitle={true}
        />
      </FlexItems>
      {organization.presentation && (
        <Expander
          isOpen={isDescriptionOpen}
          onChange={() => setIsDescriptionOpen(!isDescriptionOpen)}
        >
          <ExpanderContent closedMinHeight="150px">
            <WYSIWYGText format="filtered_html" content={presentation} />
          </ExpanderContent>
          <Spacing />
          <ExpanderTrigger>
            <Text tag="span" color="primary">
              {!isDescriptionOpen ? (
                <FormattedMessage
                  id="organization_about_expand_presentation_more"
                  defaultMessage="Show more"
                />
              ) : (
                <FormattedMessage
                  id="organization_about_expand_presentation_less"
                  defaultMessage="Show less"
                />
              )}
            </Text>
          </ExpanderTrigger>
        </Expander>
      )}
      <Spacing />
      {!trimmed && organization.size && (
        <FlexItems alignItems="flex-end">
          <FlexItem marginRight={4}>
            <OfficeIcon
              width="15"
              height="15"
              title="Organization size"
              color="gray7"
            />
          </FlexItem>
          <FlexItem marginRight={4}>
            <Text color="gray4">
              <FormattedMessage
                id="organization_size_label"
                defaultMessage="Organization size"
              />
            </Text>
          </FlexItem>
          <FlexItem>
            <Text>{organization.size.label}</Text>
          </FlexItem>
        </FlexItems>
      )}
      {!trimmed &&
        organization.occupation &&
        organization.occupation?.length > 0 && (
          <FlexItems alignItems="flex-end">
            <FlexItem marginRight={4}>
              <BriefcaseIcon
                width="15"
                height="15"
                title="Wanted occupations"
                color="gray7"
              />
            </FlexItem>
            <FlexItem marginRight={4}>
              <Text color="gray4">
                <FormattedMessage
                  id="organization_occupation_label"
                  defaultMessage="Wanted occupations"
                />
              </Text>
            </FlexItem>
            <FlexItem>
              <Text>
                <StringJoiner
                  items={organization.occupation.map((occupation) =>
                    occupation ? occupation.label : '',
                  )}
                />
              </Text>
            </FlexItem>
          </FlexItems>
        )}
      {!trimmed &&
        organization.fieldsOfStudy &&
        organization.fieldsOfStudy?.length > 0 && (
          <FlexItems alignItems="flex-end">
            <FlexItem marginRight={4}>
              <EducationIcon
                width="15"
                height="15"
                title="Wanted field of studies"
                color="gray7"
              />
            </FlexItem>
            <FlexItem marginRight={4}>
              <Text color="gray4">
                <FormattedMessage
                  id="organization_fields_of_study_label"
                  defaultMessage="Wanted field of studies"
                />
              </Text>
            </FlexItem>
            <FlexItem>
              <Text>
                <StringJoiner
                  items={organization.fieldsOfStudy.map((fieldsOfStudy) =>
                    fieldsOfStudy ? fieldsOfStudy.label : '',
                  )}
                />
              </Text>
            </FlexItem>
          </FlexItems>
        )}
      <Spacing />
      {!trimmed && (
        <OrganizationDetailAboutExtraInfoSlot organization={organization} />
      )}
      {trimmed && (
        <>
          <Spacing size="medium" />
          <Link
            href={organization?.urlNoPrefix ?? organization?.url?.path}
            asChild
          >
            <LinkButton
              linkElement={NextLink}
              type="button"
              buttonStyle="secondary"
              href={organization?.urlNoPrefix ?? organization?.url?.path}
            >
              <OfficeIcon title={organization?.name ?? 'Organization'} />
              <span>
                <FormattedMessage
                  id="organization_about_button_to_profile"
                  defaultMessage="Visit organization profile"
                />
              </span>
            </LinkButton>
          </Link>
        </>
      )}
    </Box>
  );
};
export default OrganizationAboutWidgetView;
